/**
 * 电商详情图节点 · 详情图/主图上的文案输出语言
 */

export const ECOM_DETAIL_OUTPUT_LANGUAGES = [
  { id: 'zh', label: '中文' },
  { id: 'en', label: 'English' }
]

export const ECOM_DETAIL_DEFAULT_OUTPUT_LANGUAGE = 'zh'

export function normalizeEcomDetailOutputLanguage(raw) {
  const id = String(raw || '')
    .trim()
    .toLowerCase()
  if (id === 'en' || id === 'english' || id === 'en-us' || id === 'en_us') return 'en'
  return 'zh'
}

export function isEcomDetailEnglishOutput(lang) {
  return normalizeEcomDetailOutputLanguage(lang) === 'en'
}

export function getEcomDetailOutputLanguageLabel(lang) {
  const id = normalizeEcomDetailOutputLanguage(lang)
  const hit = ECOM_DETAIL_OUTPUT_LANGUAGES.find(l => l.id === id)
  return hit ? hit.label : ECOM_DETAIL_OUTPUT_LANGUAGES[0].label
}

/** 写入 DeepSeek system / user，约束生图提示词中的可读文案语言 */
export function getEcomDetailCopyLanguageBlock(lang) {
  if (isEcomDetailEnglishOutput(lang)) {
    return [
      '【Detail image copy language · English (mandatory)】',
      'All readable marketing text that will appear ON the generated images (headlines, subheads, bullet points, badges, buttons, price callouts, spec labels, etc.) must be in English only.',
      'Do not use Chinese, Japanese, Korean, or other languages for newly designed layout copy (authentic packaging text from the reference product may remain as photographed).',
      'Typography: clean modern sans-serif English (Helvetica / Inter / SF Pro style), professional e-commerce layout, no gibberish.',
      'If the user filled product info in Chinese, translate and polish selling points into natural, conversion-oriented English before writing image prompts.'
    ].join('\n')
  }
  return [
    '【详情图文案语言 · 中文（强制）】',
    '所有生成的电商详情图、主图、卖点图上新设计的可读营销文案必须使用简体中文。',
    '字体：微软雅黑或同等清晰现代黑体，商业化排版，无乱码、无臆造品牌字。',
    '若用户用英文填写卖点，请翻译为地道、有转化力的中文后再写入生图提示词。'
  ].join('\n')
}

/** 上游商品图视觉分析指令（多模态） */
export function getEcomUpstreamImageAnalysisPrompt(lang) {
  if (isEcomDetailEnglishOutput(lang)) {
    return `You are an e-commerce product visual analyst. Based on the user's product reference image(s), output structured analysis in English for downstream image-prompt generation.

Must include:
- Category and product type
- Product name / style guess (quote packaging text if visible; otherwise "unidentified")
- Appearance: color, material, shape, structure, proportions, packaging, visible logos/text, quantity and accessories
- Style tier and target audience guess
- 3–6 convertible selling points and typical usage scenarios

Rules: Do not invent subjects, colors, brands, or accessories not in the image; mark unclear details as "unidentified". Output plain analysis text only, no markdown headings.`
  }
  return `你是电商商品视觉分析专家。请根据用户提供的商品参考图（可能多张），输出结构化中文分析，供后续按 Skill 生成电商详情图生图提示词。

必须包含：
- 品类与商品类型
- 商品名称/款式推测（若图中有包装文字可引用，看不清则写「未辨识」）
- 外观：颜色、材质、形状、结构、比例、包装、可见 logo/文字、数量与配件
- 风格档次与目标人群推测
- 3～6 条可转化的核心卖点与典型使用场景

严格要求：禁止编造图中不存在的主体、颜色、品牌或配件；看不清的细节写「未辨识」。只输出分析正文，不要 markdown 标题编号。`
}

export function getEcomTextInferFromRefsSystem(lang) {
  if (isEcomDetailEnglishOutput(lang)) {
    return `You are an e-commerce product strategist. You cannot see images directly, but receive upstream image node descriptions, reference URLs, and user product notes.
Synthesize an English product visual analysis (category, appearance, selling points, scenes). Mark unknowns as "align with reference on render"; do not invent specific brand logos.
Plain text only, no markdown headings.`
  }
  return `你是电商商品策划。用户无法直接上传图片给你，但提供了上游图片节点的文字描述、参考图 URL 列表与用户填写的商品信息。
请综合这些信息，输出与「商品视觉分析」等价的结构化中文（品类、外观推测、卖点、场景）。看不清的写「待出图对齐参考图」；不要编造具体品牌 logo。
只输出分析正文，不要 markdown 标题。`
}

export function getEcomDetailTypographyFallbackHint(lang) {
  if (isEcomDetailEnglishOutput(lang)) {
    return 'clean English marketing typography areas, modern sans-serif, no watermark'
  }
  return '微软雅黑风格中文卖点排版区、无水印'
}
